import { Box, Divider, Stack, styled, Typography } from '@mui/material'
import type { FC } from 'react'
import { useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { OwnershipDto } from '@hcr/api/consumer'
import { COLORS, rotateYCss, UNICODE, remUnit, unit } from '@hcr/ui'
import { isNotUndefined } from '@hcr/utils'

import { unitless } from '../../../../../../../../../libs/ui/src/lib/utils'
import { SvgLogoHolidayClub } from '../../../../../../assets'
import { SENTRY_CONFIG } from '../../../../../../configs'
import { useUserDetails } from '../../../../../../contexts'

import { OwnerCardLoading } from './OwnerCardLoading'

interface OwnerCardVariant {
  dark: string
  hasBadge: boolean
  letterColor: string
  letterOpacity: number
  light: string
  name: string
  text: string
}

export const OwnerCard: FC = () => {
  const { t } = useTranslation()
  const titleId = useId()

  const userDetails = useUserDetails()
  const { is_villas, is_active_gold, is_b2b_gold, is_b2b_villas } = (userDetails.data?.ownership as OwnershipDto) || {}

  const variant = useMemo<OwnerCardVariant>(() => {
    if (is_villas || is_b2b_villas) {
      return {
        dark: COLORS.sammal[600],
        hasBadge: true,
        letterColor: COLORS.ruoho[400],
        letterOpacity: unitless(0.04),
        light: COLORS.ruoho[300],
        name: t('owner.villas'),
        text: COLORS.sammal[600],
      }
    }

    if (is_active_gold || is_b2b_gold) {
      return {
        dark: COLORS.kaarna[500],
        hasBadge: true,
        letterColor: COLORS.kaarna['60%'],
        letterOpacity: unitless(0.02),
        light: COLORS.vaalea['60%'],
        name: t('owner.gold'),
        text: COLORS.tummakaarna[500],
      }
    }

    return {
      dark: COLORS.burgundi[500],
      hasBadge: false,
      letterColor: COLORS.burgundi[100],
      letterOpacity: unitless(0.18),
      light: COLORS.white,
      name: 'H',
      text: COLORS.burgundi[500],
    }
  }, [t, is_active_gold, is_villas, is_b2b_gold, is_b2b_villas])

  if (userDetails.isSuccess && isNotUndefined(userDetails.data)) {
    return (
      <Box
        component='section'
        position='relative'
        borderRadius={unit(1.5)}
        bgcolor={variant.light}
        role='region'
        aria-labelledby={titleId}
      >
        <BackgroundLetter
          color={variant.letterColor}
          opacity={variant.letterOpacity}
          css={rotateYCss}
          aria-hidden='true'
        >
          <BackgroundLetterGlyph>{variant.name.at(0)}</BackgroundLetterGlyph>
        </BackgroundLetter>
        <Box position='relative' textAlign='center'>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent={variant.hasBadge ? 'space-between' : 'start'}
            paddingLeft={unit(4)}
            paddingTop={unit(3)}
            paddingRight={unit(3)}
          >
            <SvgLogoHolidayClub height={unit(2.5)} fill={COLORS.black} aria-hidden='true' focusable='false' />
            {variant.hasBadge && (
              <Box
                bgcolor={variant.dark}
                color={COLORS.white}
                borderRadius={unit(1)}
                paddingX={unit(1.5)}
                aria-label={`${variant.name} ${t('owner.tier-level')}`}
              >
                <Badge component='span' variant='titleS' textTransform='uppercase' aria-hidden='true'>
                  {variant.name}
                </Badge>
              </Box>
            )}
          </Stack>
          <Stack paddingX={unit(4)}>
            <Title id={titleId} component='h2' color={variant.text}>
              {t('owner.owner-card')}
            </Title>
            <Divider />
            <Stack paddingTop={unit(2.5)} paddingBottom={unit(9.5)}>
              <OwnerName component='span' className={SENTRY_CONFIG.privacy.className} variant='titleM'>
                {userDetails.data.first_name} {userDetails.data.last_name}
              </OwnerName>
              <MembershipNumber
                className={SENTRY_CONFIG.privacy.className}
                variant='meta'
                aria-label={
                  userDetails.data.customer_id
                    ? `${t('owner.membership-number')}: ${userDetails.data.customer_id.split('').join(' ')}`
                    : undefined
                }
              >
                {userDetails.data.customer_id ?? UNICODE.nbsp}
              </MembershipNumber>
            </Stack>
          </Stack>
        </Box>
      </Box>
    )
  }

  return <OwnerCardLoading />
}

const Badge = styled(Typography)`
  letter-spacing: ${remUnit(0.014)};
  line-height: ${remUnit(6)};
` as typeof Typography

const BackgroundLetter = styled(Box, {
  shouldForwardProp: prop => prop !== 'opacity',
})<{ opacity: number }>`
  position: absolute;
  top: ${unit(1.25)};
  bottom: ${unit(1.25)};
  left: 0;
  right: 0;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  user-select: none;
  opacity: ${props => props.opacity};
`

// Negative margins crop Moret's oversized line-box down to the glyph's own visual height (measured), so the 5px gap on BackgroundLetter is a real 5px, not swallowed by font padding.
const BackgroundLetterGlyph = styled('span')`
  display: block;
  font-family: 'Moret', serif;
  font-size: ${remUnit(85.5)};
  font-weight: bold;
  text-align: center;
  line-height: normal;
  margin-top: ${unit(-22.75)};
  margin-bottom: ${unit(-21)};
`

const Title = styled(Box)`
  font-family: 'Moret', serif;
  font-size: ${remUnit(9.5)};
  font-weight: 700;
  line-height: ${remUnit(10)};
  text-transform: uppercase;
  padding-top: ${unit(6)};
  padding-bottom: ${unit(3)};

  ${props => props.theme.breakpoints.down('sm')} {
    font-size: ${remUnit(8)};
  }

  ${props => props.theme.breakpoints.down(350)} {
    font-size: ${remUnit(6.5)};
    word-break: break-all;
  }
` as typeof Box

const OwnerName = styled(Typography)`
  letter-spacing: 0;
  font-weight: 500;
  overflow-wrap: break-word;
` as typeof Typography

const MembershipNumber = styled(Typography)`
  letter-spacing: ${remUnit(0.06)};
` as typeof Typography
